<template>
  <div>
    <button :class="btnClass" @click="showBackgroundModal = true" class="btn">
      {{ title ?? "选择更多来源" }}
    </button>

    <VanPopup
      v-model:show="showBackgroundModal"
      class="py-4 w-full px-4"
      round
      closeable
    >
      <p class="pb-2 text-center font-bold text-lg">
        {{ title ?? "选择更多来源" }}
      </p>
      <p class="text-base-content/50 text-center mb-4">
        {{ statisticText }}
      </p>
      <div class="flex-1 flex w-full items-center pl-1 gap-2">
        <div
          :class="[
            'flex-1 flex items-center gap-1 flex-wrap justify-end',
            isVertical ? 'flex-col gap-4' : ' ',
          ]"
        >
          <button
            v-if="typeList.includes('mail')"
            class="btn btn-xs rounded"
            @click="clickOpen('mail')"
          >
            {{ $t(`mission.carryItems.selectMail`) }}
          </button>

          <button
            v-if="typeList.includes('moment')"
            class="btn btn-xs rounded"
            @click="clickOpen('moment')"
          >
            {{ $t(`mission.carryItems.selectMoment`) }}
          </button>
          <button
            v-if="typeList.includes('journal')"
            class="btn btn-xs rounded"
            @click="clickOpen('journal')"
          >
            {{ $t(`mission.carryItems.selectJournal`) }}
          </button>
          <button
            v-if="typeList.includes('dispatch')"
            class="btn btn-xs rounded"
            @click="clickOpen('dispatch')"
          >
            {{ $t(`mission.carryItems.selectDispatch`) }}
          </button>
          <!-- <button
        v-if="typeList.includes('roleplayAnswer')"
        class="btn btn-xs rounded"
        @click="clickOpen('roleplayAnswer')"
      >
        {{ $t(`mission.carryItems.selectRoleplayAnswer`) }}
      </button> -->

          <button
            v-if="typeList.includes('memory')"
            class="btn btn-xs rounded"
            @click="clickOpen('memory')"
          >
            {{ $t(`mission.carryItems.selectSceneMemory`) }}
          </button>
        </div>
      </div>
    </VanPopup>

    <VanPopup
      v-model:show="showPopup"
      position="bottom"
      class="h-[80%] flex flex-col overflow-hidden font-[Cubic]"
      teleport="#main-view"
      @wheel="(e: WheelEvent) => e.stopPropagation()"
      @touchmove="(e: TouchEvent) => e.stopPropagation()"
    >
      <div class="flex gap-2 justify-end pt-2 pr-2">
        <button class="btn btn-outline btn-xs" @click="showPopup = false">
          {{ $t(`Buttons.cancel`) }}
        </button>
        <button class="btn btn-primary btn-xs" @click="clickConfirm">
          {{ $t(`Buttons.confirm`) }}
        </button>
      </div>

      <div class="flex-1 overflow-hidden">
        <MissionJourneyList
          v-if="selectType === 'journal'"
          key="journal"
          :include="includeJourneyzz.map((v) => v.id)"
          :mate-id="mateId"
          is-select
          @select="selectJournal"
          class="pt-2"
        />
        <MateMailList
          v-else-if="selectType === 'mail'"
          key="mail"
          :mate-id="mateId"
          :include="includeMailzz.map((v) => v.id)"
          is-select
          @select="selectMail"
          :recipient-id="recipientId"
        />
        <MissionDispatchSelect
          v-else-if="selectType === 'dispatch'"
          key="dispatch"
          :mate-id="mateId"
          :status="MateDispatchStatusRequest.Settled"
          :include="includeDispatchzz.map((v) => v.id)"
          is-select
          @select="selectDispatch"
        />
        <MateMemoryList
          v-else-if="selectType === 'memory'"
          key="memory"
          :mate-id="mateId"
          :include="includeMemoryzz.map((v) => v.id)"
          is-select
          @select="selectMemory"
        />
        <MomentSelect
          v-else-if="selectType === 'moment'"
          key="moment"
          :mate-id="mateId"
          :include="includeMomentzz.map((v) => v.id)"
          @select="selectMoment"
        />
      </div>
    </VanPopup>
  </div>
</template>

<script setup lang="ts">
import {
  checkAndInitObjective,
  isSame,
} from "@/components/Mission/MissionGenerate";
import { MateDispatchStatusRequest } from "@/enums/mission";
import {
  MateDispatch,
  MateJournal,
  MateMail,
  MateMemory,
  MateRoleplayAnswer,
  Moment,
} from "@/types/entity";
import { computed, nextTick, ref } from "vue";

type SelectType =
  | "journal"
  | "mail"
  | "dispatch"
  | "memory"
  | "moment"
  | "roleplayAnswer";

const props = withDefaults(
  defineProps<{
    title?: string;
    btnClass?: string;
    mateId: number;
    recipientId?: number;
    typeList?: SelectType[];
    isVertical?: boolean;

    journalzz?: MateJournal[];
    mailzz?: MateMail[];
    dispatchzz?: MateDispatch[];
    memoryzz?: MateMemory[];
    momentzz?: Moment[];
    roleplayAnswerzz?: MateRoleplayAnswer[];

    dispatchMission?: MateDispatch;
  }>(),
  {
    typeList: () => ["journal", "mail", "dispatch", "memory"],
  }
);

const emit = defineEmits<{
  (e: "update:journalzz", journalzz: MateJournal[]): void;
  (e: "update:mailzz", mailzz: MateMail[]): void;
  (e: "update:dispatchzz", dispatchzz: MateDispatch[]): void;
  (e: "update:memoryzz", memoryzz: MateMemory[]): void;
  (e: "update:momentzz", momentzz: Moment[]): void;
  (e: "update:roleplayAnswerzz", roleplayAnswerzz: MateRoleplayAnswer[]): void;
}>();

const showBackgroundModal = ref(false);

const selectType = ref<SelectType | undefined>(undefined);
const includeJourneyzz = ref<MateJournal[]>([]);
const includeMailzz = ref<MateMail[]>([]);
const includeDispatchzz = ref<MateDispatch[]>([]);
const includeMemoryzz = ref<MateMemory[]>([]);
const includeMomentzz = ref<Moment[]>([]);
const includeRoleplayAnswerzz = ref<MateRoleplayAnswer[]>([]);
const showPopup = ref(false);

const clickOpen = (type: SelectType) => {
  selectType.value = undefined;
  nextTick(() => {
    selectType.value = type;
    showPopup.value = true;
    includeJourneyzz.value = [...(props.journalzz || [])];
    includeMailzz.value = [...(props.mailzz || [])];
    includeDispatchzz.value = [...(props.dispatchzz || [])];
    includeMemoryzz.value = [...(props.memoryzz || [])];
    includeMomentzz.value = [...(props.momentzz || [])];
  });
};

const clickConfirm = async () => {
  if (
    props.dispatchMission &&
    ((selectType.value === "journal" &&
      !isSame(includeJourneyzz.value, props.journalzz || [])) ||
      (selectType.value === "mail" &&
        !isSame(includeMailzz.value, props.mailzz || [])) ||
      (selectType.value === "dispatch" &&
        !isSame(includeDispatchzz.value, props.dispatchzz || [])) ||
      (selectType.value === "memory" &&
        !isSame(includeMemoryzz.value, props.memoryzz || [])))
  ) {
    const pass = await checkAndInitObjective(props.dispatchMission);
    if (!pass) return;
  }

  if (selectType.value === "journal") {
    emit("update:journalzz", includeJourneyzz.value);
  } else if (selectType.value === "mail") {
    emit("update:mailzz", includeMailzz.value);
  } else if (selectType.value === "dispatch") {
    emit("update:dispatchzz", includeDispatchzz.value);
  } else if (selectType.value === "memory") {
    emit("update:memoryzz", includeMemoryzz.value);
  } else if (selectType.value === "moment") {
    emit("update:momentzz", includeMomentzz.value);
  } else if (selectType.value === "roleplayAnswer") {
    emit("update:roleplayAnswerzz", includeRoleplayAnswerzz.value);
  }

  showPopup.value = false;
};

const selectJournal = (journal: MateJournal) => {
  const idx = includeJourneyzz.value.findIndex((v) => v.id === journal.id);
  if (idx !== -1) {
    includeJourneyzz.value.splice(idx, 1);
  } else {
    includeJourneyzz.value.push(journal);
  }
};

const selectMail = (mail: MateMail) => {
  const idx = includeMailzz.value.findIndex((v) => v.id === mail.id);
  if (idx !== -1) {
    includeMailzz.value.splice(idx, 1);
  } else {
    includeMailzz.value.push(mail);
  }
};

const selectDispatch = (dispatch: MateDispatch) => {
  const idx = includeDispatchzz.value.findIndex((v) => v.id === dispatch.id);
  if (idx !== -1) {
    includeDispatchzz.value.splice(idx, 1);
  } else {
    includeDispatchzz.value.push(dispatch);
  }
};

const selectMemory = (memory: MateMemory) => {
  const idx = includeMemoryzz.value.findIndex((v) => v.id === memory.id);
  if (idx !== -1) {
    includeMemoryzz.value.splice(idx, 1);
  } else {
    includeMemoryzz.value.push(memory);
  }
};

const selectMoment = (moment: Moment) => {
  const idx = includeMomentzz.value.findIndex((v) => v.id === moment.id);
  if (idx !== -1) {
    includeMomentzz.value.splice(idx, 1);
  } else {
    includeMomentzz.value.push(moment);
  }
};

const statisticText = computed(() => {
  return [
    { text: "信件", count: props.mailzz?.length },
    { text: "随记", count: props.momentzz?.length },
    {
      text: "手账",
      count: props.journalzz?.length,
    },
    { text: "任务", count: props.dispatchzz?.length },
    { text: "记忆", count: props.memoryzz?.length },
    { text: "问答", count: props.roleplayAnswerzz?.length },
  ]
    .filter((item) => item.count)
    .map((item) => `${item.text}(${item.count})`)
    .join("，");
});
</script>
